(() => {
  class LovelyGameDeck extends HTMLElement {
    constructor() {
      super();
      this.config = {};
      this.cards = [];
      this.state = this.freshState();
      this.currentScreen = 'intro';
      this.timerInterval = null;
      this.timerRemaining = 0;
      this.timerRunning = false;
      this.timerEndsAt = 0;
      this.audioContext = null;
      this.wakeLock = null;
      this.settingsMode = false;
      this.settingsStartsNewGame = false;
      this.settingsReturnScreen = 'play';
      this.settingsSnapshot = '';
      this.gameModeActive = false;
      this.privateDraftPermissions = {};
      this.analyticsLevelsSeen = new Set();
      this.scrollFrame = null;
      this.previousScrollRestoration = null;
      this.sessionKey = `lovely-game-session-v12:${window.location.pathname}`;
      this.handlePageShow = () => this.resetInitialScroll();
      this.handleVisibilityChange = () => this.onVisibilityChange();
      this.handleBeforeUnload = (event) => this.onBeforeUnload(event);
      this.handlePageHide = () => this.persistSession();
    }

    async connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';
      try {
        this.config = JSON.parse(this.querySelector('[data-lovely-game-config]').textContent);
        this.applyAdminData(JSON.parse(this.querySelector('[data-lovely-game-admin]').textContent));
        this.cards = JSON.parse(this.querySelector('[data-lovely-game-cards]').textContent).map((card, index) => this.normalizeCard(card, index));
      } catch (error) {
        console.error('Lovely game data could not be loaded.', error);
        return;
      }
      this.cacheElements();
      this.applyConfiguration();
      this.bindEvents();
      this.renderProfiles();
      this.showScreen('intro', { scroll: false });
      this.prepareInitialScroll();
      await this.loadAdditionalCards();
      this.offerSessionRestore();
    }

    disconnectedCallback() {
      this.clearTimerInterval();
      this.releaseWakeLock();
      this.setGameMode(false);
      if (this.scrollFrame) window.cancelAnimationFrame(this.scrollFrame);
      window.removeEventListener('pageshow', this.handlePageShow);
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
      window.removeEventListener('beforeunload', this.handleBeforeUnload);
      window.removeEventListener('pagehide', this.handlePageHide);
      if (this.previousScrollRestoration) window.history.scrollRestoration = this.previousScrollRestoration;
    }

    prepareInitialScroll() {
      if ('scrollRestoration' in window.history) {
        this.previousScrollRestoration = window.history.scrollRestoration;
        window.history.scrollRestoration = 'manual';
      }
      window.addEventListener('pageshow', this.handlePageShow);
      this.resetInitialScroll();
    }

    resetInitialScroll() {
      const reset = () => window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      reset();
      window.requestAnimationFrame(() => window.requestAnimationFrame(reset));
    }

    freshState() {
      return {
        players: [0, 1].map(() => ({ name: '', bodyProfile: '', anatomy: new Set(), permissions: {}, maxLevel: 1 })),
        setupPlayer: 0,
        equipment: new Set(),
        effectiveMax: 1,
        gameLength: 10,
        levelPlan: [],
        currentPlanIndex: 0,
        currentLevel: 1,
        levelProgress: 0,
        currentCard: null,
        usedIds: new Set(),
        cardsShown: 0,
        plannedTotal: 10,
        phase: 'standard',
        startedAt: 0,
        hasFinale: true,
        lastActorIndex: null,
        lastCardTheme: ''
      };
    }

    applyAdminData(admin) {
      const ordered = (items) => [...items].sort((a, b) => Number(a.order || 0) - Number(b.order || 0));
      ['bodyProfiles', 'anatomy', 'boundaryCategories', 'activities', 'equipmentGroups', 'equipment', 'levels', 'lengths'].forEach((key) => {
        if (admin[key]?.length) this.config[key] = ordered(admin[key]);
      });
      const supportedAnatomy = new Set(['vulva', 'penis']);
      this.config.anatomy = this.config.anatomy.filter((item) => supportedAnatomy.has(item.key));
      this.config.bodyProfiles = this.config.bodyProfiles.map((profile) => ({
        ...profile,
        traits: (profile.traits || []).filter((key) => supportedAnatomy.has(key))
      }));
      this.config.adminSettings = admin.settings || {};
      if (this.config.adminSettings.noCards) this.config.copy.noCards = this.config.adminSettings.noCards;
    }

    applyConfiguration() {
      const settings = this.config.adminSettings || {};
      const setText = (selector, value) => { const node = this.querySelector(selector); if (node && value) node.textContent = value; };
      setText('[data-consent-reminder]', settings.consentReminder);
      if (settings.playerOneColor) this.style.setProperty('--lg-player-one', settings.playerOneColor);
      if (settings.playerTwoColor) this.style.setProperty('--lg-player-two', settings.playerTwoColor);
    }

    normalizeList(value) {
      if (Array.isArray(value)) return value.map((item) => typeof item === 'object' ? item.key : item).filter(Boolean);
      if (typeof value === 'string') return value.split(',').map((item) => item.trim()).filter(Boolean);
      return [];
    }

    normalizeCard(card, index) {
      return {
        ...card,
        id: card.id || `card-${index + 1}`,
        number: card.number || `L${String(index + 1).padStart(4, '0')}`,
        stage: card.stage || 'standard',
        actorBoundaries: this.normalizeList(card.actorBoundaries),
        receiverBoundaries: this.normalizeList(card.receiverBoundaries),
        actorAnatomy: this.normalizeList(card.actorAnatomy),
        receiverAnatomy: this.normalizeList(card.receiverAnatomy),
        equipmentAll: this.normalizeList(card.equipmentAll),
        equipmentAny: this.normalizeList(card.equipmentAny),
        shopUrls: this.normalizeList(card.shopUrls || card.shopUrl),
        weight: Math.max(1, Number(card.weight || 1))
      };
    }

    cacheElements() {
      const one = (selector) => this.querySelector(selector);
      this.screens = [...this.querySelectorAll('[data-screen]')];
      this.stage = one('[data-game-stage]');
      this.progress = one('[data-game-progress]'); this.progressBar = one('[data-game-progress-bar]'); this.progressLabel = one('[data-game-progress-label]');
      this.nameInputs = [...this.querySelectorAll('[data-player-name]')]; this.nameError = one('[data-name-error]');
      this.privateLevelTitle = one('[data-private-level-title]'); this.privateLevelBody = one('[data-private-level-body]'); this.privateLevelList = one('[data-private-level-list]');
      this.privateBoundaries = one('[data-private-boundaries]'); this.noPrivateBoundaries = one('[data-no-private-boundaries]'); this.privateBoundaryNote = one('[data-private-boundary-note]');
      this.consentList = one('[data-consent-list]'); this.consentToggle = one('[data-consent-toggle]'); this.handoffTitle = one('[data-handoff-title]');
      this.equipmentList = one('[data-equipment-list]'); this.lengthList = one('[data-length-list]');
      this.equipmentSummary = one('[data-equipment-summary]'); this.equipmentToggle = one('[data-equipment-toggle]'); this.playerHeadings = [...this.querySelectorAll('[data-player-heading]')];
      this.settingsEquipmentButton = one('[data-action="settings-equipment"]');
      this.loadStatus = one('[data-load-status]'); this.beginButton = one('[data-action="begin"]'); this.retryCardsButton = one('[data-action="retry-cards"]');
      this.playCount = one('[data-play-count]'); this.currentLevelName = one('[data-current-level-name]'); this.currentLevelDescription = one('[data-current-level-description]');
      this.advanceLevelButton = one('[data-action="advance-level"]'); this.finaleButton = one('[data-action="start-finale"]');
      this.levelProgress = one('[data-level-progress]');
      this.gameCard = one('[data-game-card]'); this.cardCategory = one('[data-card-category]'); this.cardNumber = one('[data-card-number]'); this.cardContext = one('[data-card-context]'); this.cardText = one('[data-card-text]'); this.cardMeta = one('[data-card-meta]');
      this.timer = one('[data-timer]'); this.timerDisplay = one('[data-timer-display]'); this.timerButton = one('[data-action="timer-toggle"]');
      this.skipButton = one('[data-action="skip"]'); this.nextButton = one('[data-action="next-card"]'); this.emptyMessage = one('[data-empty-message]'); this.exhaustedMessage = one('[data-exhausted-message]');
      this.completeMessage = one('[data-complete-message]'); this.restoreSummary = one('[data-restore-summary]');
      this.exitDialog = one('[data-exit-dialog]'); this.exitReturnFocus = null;
    }

    bindEvents() {
      this.addEventListener('click', (event) => {
        const shopLink = event.target.closest('.lovely-game__shop-link');
        if (shopLink && this.contains(shopLink)) {
          this.publishGameEvent('product_click', {
            card_number: this.state.currentCard?.number || '',
            card_level: Number(this.state.currentCard?.level || 0),
            card_stage: this.state.phase,
            card_position: Number(this.state.cardsShown || 0),
            link_url: shopLink.href
          });
        }
        const button = event.target.closest('[data-action]');
        if (!button || !this.contains(button)) return;
        const actions = {
          begin: () => { this.publishGameEvent('setup_start'); this.showScreen('names'); }, back: () => this.showScreen('intro'), 'save-names': () => this.saveNames(),
          'private-level-back': () => this.privateLevelBack(), 'save-private-level': () => this.savePrivateLevel(), 'next-player': () => this.nextPlayer(),
          'toggle-consent-all': () => this.toggleAllChoices(this.consentList, '[data-permission-key]', this.consentToggle, () => this.updateConsentCounts()),
          'equipment-back': () => this.settingsMode ? this.showScreen('settings') : this.returnToSecondPrivateChoice(), 'save-equipment': () => this.saveEquipment(),
          'toggle-equipment-all': () => this.toggleAllChoices(this.equipmentList, '[data-equipment-key]', this.equipmentToggle, () => this.updateEquipmentCounts()),
          'length-back': () => this.settingsMode ? this.showScreen('settings') : this.returnFromLength(), 'save-length': () => this.saveLength(),
          'next-card': () => this.consumeCard(), skip: () => this.skipCard(), 'timer-toggle': () => this.toggleTimer(),
          'advance-level': () => this.advanceLevel(), 'start-finale': () => this.startFinale(this.state.currentLevel),
          'open-settings': () => this.openSettings(), 'settings-back': () => this.closeSettings(false), 'settings-boundaries': () => this.editPrivateChoices(),
          'settings-equipment': () => { this.renderEquipment(); this.showScreen('equipment'); }, 'settings-length': () => { this.renderLengths(); this.showScreen('length'); },
          'resume-game': () => this.closeSettings(), replay: () => this.replayGame(), 'edit-and-replay': () => this.editBeforeReplay(), reset: () => this.resetGame(), 'exit-game': () => this.requestExitGame(), 'cancel-exit': () => this.closeExitDialog(), 'confirm-exit': () => this.resetGame(), 'empty-back': () => this.openSettings(),
          'restore-session': () => this.restoreSession(), 'discard-session': () => this.discardSession(),
          'retry-cards': () => this.loadAdditionalCards(true), 'continue-exhausted': () => this.continueAfterExhaustion()
        };
        if (actions[button.dataset.action]) actions[button.dataset.action]();
      });
      this.addEventListener('keydown', (event) => { if (event.key === 'Escape' && !this.exitDialog?.hidden) this.closeExitDialog(); else this.handleGameModeKeydown(event); });
      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-body-profile]')) this.toggleCustomAnatomy(Number(event.target.dataset.bodyProfile), event.target.value === 'custom');
        if (event.target.matches('[data-private-level]')) this.updatePrivateLevelAndBoundaries();
        if (event.target.matches('[data-permission-key]')) this.updateConsentCounts();
        if (event.target.matches('[data-equipment-key]')) this.updateEquipmentCounts();
        if (event.target.matches('[data-game-length]')) this.updateLengthStates();
      });
      this.nameInputs.forEach((input, index) => input.addEventListener('input', () => { this.nameError.hidden = true; this.playerHeadings[index].textContent = input.value.trim() || `Aðili ${index + 1}`; }));
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
      window.addEventListener('beforeunload', this.handleBeforeUnload);
      window.addEventListener('pagehide', this.handlePageHide);
    }

    publishGameEvent(name, data = {}) {
      const payload = {};
      Object.entries(data).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') return;
        payload[key] = value;
      });
      try {
        if (window.Shopify?.analytics?.publish) window.Shopify.analytics.publish(`lovely_game:${name}`, payload);
      } catch (error) {}
    }

    gameElapsedSeconds() {
      if (!this.state.startedAt) return 0;
      return Math.max(0, Math.round((Date.now() - Number(this.state.startedAt)) / 1000));
    }

    levelName(value) {
      return this.config.levels.find((level) => Number(level.value) === Number(value))?.name || '';
    }

    publishSelectionAnalytics(configurationStage = 'start') {
      this.config.equipment.forEach((item) => {
        this.publishGameEvent('equipment_status', {
          configuration_stage: configurationStage,
          equipment_key: item.key,
          equipment_group: item.group || '',
          available: this.equipmentVisible(item) ? 1 : 0,
          selected: this.state.equipment.has(item.key) ? 1 : 0
        });
      });
      this.config.activities.forEach((activity) => {
        const permissions = this.state.players.map((player) => player.permissions[activity.key] || {});
        const availableByPlayer = this.state.players.map((player, index) => {
          const partner = this.state.players[1 - index];
          return {
            give: activity.selectionMode !== 'personal' && this.directionAvailable(activity, player, partner, 'give'),
            receive: activity.selectionMode !== 'personal' && this.directionAvailable(activity, player, partner, 'receive'),
            personal: activity.selectionMode === 'personal' && this.directionAvailable(activity, player, partner, 'personal')
          };
        });
        const giveCount = permissions.filter((permission) => permission.give).length;
        const receiveCount = permissions.filter((permission) => permission.receive).length;
        const personalCount = permissions.filter((permission) => permission.personal).length;
        const giveAvailableCount = availableByPlayer.filter((item) => item.give).length;
        const receiveAvailableCount = availableByPlayer.filter((item) => item.receive).length;
        const personalAvailableCount = availableByPlayer.filter((item) => item.personal).length;
        const selectedCount = activity.selectionMode === 'personal' ? personalCount : giveCount + receiveCount;
        const availableCount = activity.selectionMode === 'personal' ? personalAvailableCount : giveAvailableCount + receiveAvailableCount;
        this.publishGameEvent('boundary_status', {
          configuration_stage: configurationStage,
          boundary_key: activity.key,
          selection_mode: activity.selectionMode || 'directional',
          available: Number(activity.minLevel || 1) <= this.state.effectiveMax && availableCount > 0 ? 1 : 0,
          available_count: availableCount,
          give_count: giveCount,
          receive_count: receiveCount,
          personal_count: personalCount,
          selected_count: selectedCount,
          selected: selectedCount > 0 ? 1 : 0
        });
      });
    }

    publishGameComplete(completionType) {
      this.publishGameEvent('complete', {
        completion_type: completionType,
        game_length: Number(this.state.gameLength || 0),
        maximum_level: Number(this.state.effectiveMax || 0),
        maximum_level_name: this.levelName(this.state.effectiveMax),
        cards_shown: Number(this.state.cardsShown || 0),
        elapsed_seconds: this.gameElapsedSeconds(),
        finale_shown: this.state.phase === 'finale' ? 1 : 0
      });
    }

    showScreen(name, options = {}) {
      if (this.currentScreen === 'play' && name !== 'play') this.pauseTimer();
      this.currentScreen = name;
      this.screens.forEach((screen) => { const active = screen.dataset.screen === name; screen.hidden = !active; screen.classList.toggle('is-active', active); });
      const steps = { names: 1, 'private-level': 2, handoff: 2, equipment: 3, length: 3 };
      const step = steps[name]; this.progress.hidden = !step;
      if (step) { this.progressBar.style.width = `${(step / 3) * 100}%`; this.progressLabel.textContent = `Uppsetning · skref ${step} af 3`; }
      const screen = this.screens.find((item) => item.dataset.screen === name);
      if (this.stage) {
        if (this.gameModeActive) { this.stage.setAttribute('role', 'dialog'); this.stage.setAttribute('aria-modal', 'true'); this.stage.setAttribute('aria-label', 'Lovely Leikurinn'); }
        else { this.stage.removeAttribute('role'); this.stage.removeAttribute('aria-modal'); this.stage.removeAttribute('aria-label'); }
      }
      if (screen && options.scroll !== false && !this.gameModeActive) this.queueScroll(step ? this.progress : screen);
      if (this.gameModeActive) this.persistSession();
    }

    queueScroll(target) {
      if (!target) return;
      if (this.scrollFrame) window.cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = window.requestAnimationFrame(() => {
        this.scrollFrame = window.requestAnimationFrame(() => {
          const header = document.querySelector('sticky-header,.header-wrapper');
          const headerStyle = header ? window.getComputedStyle(header) : null;
          const headerOffset = header && ['fixed', 'sticky'].includes(headerStyle?.position) ? header.getBoundingClientRect().height + 12 : 16;
          const top = Math.max(0, target.getBoundingClientRect().top + window.scrollY - headerOffset);
          window.scrollTo({ top, left: 0, behavior: 'auto' });
          this.scrollFrame = null;
        });
      });
    }

    setGameMode(active) {
      this.gameModeActive = Boolean(active);
      this.classList.toggle('is-game-mode', this.gameModeActive);
      document.documentElement.classList.toggle('lovely-game-modal-open', this.gameModeActive);
      document.body?.classList.toggle('lovely-game-modal-open', this.gameModeActive);
      if (this.gameModeActive) this.requestWakeLock(); else this.releaseWakeLock();
      if (!this.gameModeActive && this.stage) { this.stage.removeAttribute('role'); this.stage.removeAttribute('aria-modal'); this.stage.removeAttribute('aria-label'); }
    }

    handleGameModeKeydown(event) {
      if (!this.gameModeActive || event.key !== 'Tab' || !this.stage) return;
      const focusRoot = this.exitDialog && !this.exitDialog.hidden ? this.exitDialog : this.stage;
      const focusable = [...focusRoot.querySelectorAll('button:not([disabled]):not([hidden]),a[href],input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])')].filter((item) => item.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0]; const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }

    async loadAdditionalCards(retry = false) {
      const pageNode = this.querySelector('[data-lovely-game-page]');
      if (!pageNode) return;
      let pageInfo;
      try { pageInfo = JSON.parse(pageNode.textContent); } catch (error) { return; }
      const lastPage = Math.min(Number(pageInfo.pages || 1), Math.ceil(Number(pageInfo.limit || 1000) / 250));
      if (lastPage <= 1) { this.setCardLoadingState('ready'); return; }
      if (!retry && this.dataset.cardsLoaded === 'true') return;
      this.setCardLoadingState('loading');
      try {
        const loaded = new Map(this.cards.map((card) => [card.id, card]));
        for (let page = 2; page <= lastPage; page += 1) {
          const url = new URL(window.location.href); url.searchParams.set('section_id', this.dataset.sectionId); url.searchParams.set(pageInfo.pageParam || 'page', String(page));
          const response = await fetch(url.toString(), { credentials: 'same-origin' }); if (!response.ok) throw new Error(`Card page ${page} failed`);
          const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html'); const data = documentFragment.querySelector('[data-lovely-game-cards]');
          if (!data) throw new Error(`Card page ${page} was empty`);
          JSON.parse(data.textContent).forEach((card, index) => { const normalized = this.normalizeCard(card, loaded.size + index); loaded.set(normalized.id, normalized); });
        }
        this.cards = [...loaded.values()].slice(0, Number(pageInfo.limit || 1000)); this.dataset.cardsLoaded = 'true'; this.setCardLoadingState('ready');
      } catch (error) { console.error('Lovely game cards could not all be loaded.', error); this.setCardLoadingState('error'); }
    }

    setCardLoadingState(state) {
      const loading = state === 'loading'; const failed = state === 'error'; this.beginButton.disabled = loading || failed; this.retryCardsButton.hidden = !failed;
      this.loadStatus.hidden = state === 'ready'; this.loadStatus.textContent = loading ? this.config.copy.loadingCards : failed ? this.config.copy.loadError : '';
    }

    makeChoice({ id, label, description = '', type = 'checkbox', name = '', value = '', checked = false, dataset = {} }) {
      const wrapper = document.createElement('label'); wrapper.className = 'lovely-game__choice'; wrapper.setAttribute('for', id);
      const input = document.createElement('input'); input.type = type; input.id = id; input.name = name; input.value = value; input.checked = checked;
      Object.entries(dataset).forEach(([key, val]) => { input.dataset[key] = val; });
      const mark = document.createElement('span'); mark.className = 'lovely-game__choice-mark'; mark.setAttribute('aria-hidden', 'true');
      const content = document.createElement('span'); content.className = 'lovely-game__choice-content'; const text = document.createElement('strong'); text.textContent = label; content.appendChild(text);
      if (description) { const help = document.createElement('small'); help.textContent = description; content.appendChild(help); }
      wrapper.append(input, mark, content); return wrapper;
    }

    renderProfiles() {
      this.config.bodyProfiles.forEach((profile) => { if (!profile.traits?.length && profile.key !== 'custom') profile.key = 'custom'; });
      [0, 1].forEach((index) => {
        const profileRoot = this.querySelector(`[data-body-profile-options="${index}"]`); const anatomyRoot = this.querySelector(`[data-anatomy-options="${index}"]`);
        profileRoot.replaceChildren(); anatomyRoot.replaceChildren();
        this.config.bodyProfiles.forEach((profile) => { const choice = this.makeChoice({ id: `${this.dataset.sectionId}-profile-${index}-${profile.key}`, label: profile.label, type: 'radio', name: `${this.dataset.sectionId}-profile-${index}`, value: profile.key, checked: this.state.players[index].bodyProfile === profile.key, dataset: { bodyProfile: index } }); choice.classList.add('lovely-game__profile-choice'); profileRoot.appendChild(choice); });
        this.config.anatomy.forEach((trait) => anatomyRoot.appendChild(this.makeChoice({ id: `${this.dataset.sectionId}-anatomy-${index}-${trait.key}`, label: trait.label, value: trait.key, checked: this.state.players[index].anatomy.has(trait.key), dataset: { anatomyPlayer: index } })));
        this.toggleCustomAnatomy(index, this.state.players[index].bodyProfile === 'custom');
      });
    }

    toggleCustomAnatomy(index, visible) { const node = this.querySelector(`[data-custom-anatomy="${index}"]`); if (node) node.hidden = !visible; }

    saveNames() {
      const names = this.nameInputs.map((input) => input.value.trim());
      if (names.some((name) => !name)) return this.showNameError(this.config.copy.nameRequired);
      if (names[0].toLocaleLowerCase() === names[1].toLocaleLowerCase()) return this.showNameError(this.config.copy.sameNames);
      for (let index = 0; index < 2; index += 1) {
        const profileKey = this.querySelector(`[data-body-profile="${index}"]:checked`)?.value;
        if (!profileKey) return this.showNameError(this.config.copy.bodyRequired);
        const profile = this.config.bodyProfiles.find((item) => item.key === profileKey); let traits = profile?.traits || [];
        if (profileKey === 'custom') traits = [...this.querySelectorAll(`[data-anatomy-player="${index}"]:checked`)].map((input) => input.value);
        if (!traits.length) return this.showNameError(this.config.copy.customBodyRequired);
        Object.assign(this.state.players[index], { name: names[index], bodyProfile: profileKey, anatomy: new Set(traits) });
      }
      this.nameError.hidden = true; this.state.setupPlayer = 0; this.showPrivateLevel();
    }

    showNameError(message) { this.nameError.textContent = message; this.nameError.hidden = false; }

    showPrivateLevel() {
      const player = this.state.players[this.state.setupPlayer];
      this.privateDraftPermissions = this.clonePermissions(player.permissions);
      this.privateLevelTitle.textContent = this.replaceTokens(this.config.copy.privateLevelFor, { name: player.name }); this.privateLevelBody.textContent = this.config.copy.privateLevelBody; this.privateLevelList.replaceChildren();
      this.config.levels.filter((level) => Number(level.value) > 0).forEach((level) => {
        const choice = this.makeChoice({ id: `${this.dataset.sectionId}-level-${this.state.setupPlayer}-${level.value}`, label: level.name, description: level.description, type: 'radio', name: `${this.dataset.sectionId}-level-${this.state.setupPlayer}`, value: level.value, checked: Number(player.maxLevel) === Number(level.value), dataset: { privateLevel: this.state.setupPlayer } });
        const status = document.createElement('span'); status.className = 'lovely-game__level-choice-status'; choice.appendChild(status); choice.classList.add('lovely-game__level-choice-v4'); this.privateLevelList.appendChild(choice);
      });
      this.updateLevelChoiceStates(); this.renderConsent(Number(player.maxLevel || 1));
      this.showScreen('private-level');
    }

    updateLevelChoiceStates() {
      const selected = Number(this.privateLevelList.querySelector('input:checked')?.value || 1);
      this.privateLevelList.querySelectorAll('[data-private-level]').forEach((input) => {
        const choice = input.closest('.lovely-game__choice'); const value = Number(input.value); choice.classList.toggle('is-included', value < selected); choice.classList.toggle('is-maximum', value === selected);
        choice.querySelector('.lovely-game__level-choice-status').textContent = value < selected ? 'Innifalið' : value === selected ? 'Valið hámark' : '';
      });
    }

    updatePrivateLevelAndBoundaries() {
      this.capturePrivateDraftPermissions();
      this.updateLevelChoiceStates();
      const selected = Number(this.privateLevelList.querySelector('input:checked')?.value || 1);
      this.renderConsent(selected);
    }

    clonePermissions(permissions = {}) {
      const copy = {};
      this.config.activities.forEach((activity) => { const value = permissions[activity.key] || {}; copy[activity.key] = { give: Boolean(value.give), receive: Boolean(value.receive), personal: Boolean(value.personal) }; });
      return copy;
    }

    capturePrivateDraftPermissions() {
      this.consentList.querySelectorAll('[data-permission-key]').forEach((input) => {
        const key = input.dataset.permissionKey; const direction = input.dataset.permissionDirection;
        if (!this.privateDraftPermissions[key]) this.privateDraftPermissions[key] = { give: false, receive: false, personal: false };
        this.privateDraftPermissions[key][direction] = input.checked;
      });
    }

    privateLevelBack() { this.showScreen(this.state.setupPlayer === 1 ? 'handoff' : (this.settingsMode ? 'settings' : 'names')); }
    savePrivateLevel() {
      this.capturePrivateDraftPermissions();
      const selectedLevel = Number(this.privateLevelList.querySelector('input:checked')?.value || 1); const player = this.state.players[this.state.setupPlayer];
      player.maxLevel = selectedLevel;
      player.permissions = this.clonePermissions();
      this.config.activities.filter((activity) => Number(activity.minLevel || 1) <= selectedLevel).forEach((activity) => {
        if (this.privateDraftPermissions[activity.key]) player.permissions[activity.key] = { ...this.privateDraftPermissions[activity.key] };
      });
      if (this.state.setupPlayer === 0) { this.handoffTitle.textContent = this.replaceTokens(this.config.copy.passDevice, { name: this.state.players[1].name }); this.showScreen('handoff'); return; }
      this.updateEffectiveMax();
      if (this.settingsMode) { this.cleanEquipment(); this.updateSettingsAvailability(); this.showScreen('settings'); } else this.goToSharedChoices();
    }
    hasAnatomy(player, required) { return !required?.length || required.every((key) => player.anatomy.has(key)); }

    directionAvailable(activity, player, partner, direction) {
      if (activity.selectionMode === 'personal') return this.hasAnatomy(player, activity.receiverAnatomy) && this.hasAnatomy(player, activity.giverAnatomy);
      const giver = direction === 'give' ? player : partner; const receiver = direction === 'give' ? partner : player;
      return this.hasAnatomy(giver, activity.giverAnatomy) && this.hasAnatomy(receiver, activity.receiverAnatomy);
    }

    renderConsent(maxLevel = this.state.players[this.state.setupPlayer].maxLevel) {
      const player = this.state.players[this.state.setupPlayer]; const partner = this.state.players[1 - this.state.setupPlayer];
      this.consentList.replaceChildren();
      let visibleCategoryCount = 0;
      this.config.boundaryCategories.forEach((category) => {
        const visible = this.config.activities.filter((activity) => activity.category === category.key && Number(activity.minLevel || 1) <= maxLevel).filter((activity) => activity.selectionMode === 'personal' ? this.directionAvailable(activity, player, partner, 'personal') : this.directionAvailable(activity, player, partner, 'give') || this.directionAvailable(activity, player, partner, 'receive'));
        if (!visible.length) return;
        const details = document.createElement('details'); details.className = 'lovely-game__consent-group'; details.open = visibleCategoryCount === 0; visibleCategoryCount += 1;
        const summary = document.createElement('summary'); const summaryText = document.createElement('span'); const title = document.createElement('strong'); title.textContent = category.label; const description = document.createElement('small'); description.textContent = category.description || ''; summaryText.append(title, description); summary.appendChild(summaryText);
        const status = document.createElement('span'); status.className = 'lovely-game__group-status'; summary.appendChild(status);
        const key = document.createElement('div'); key.className = 'lovely-game__consent-key'; key.append(document.createElement('span'));
        ['Gefa eða gera', 'Þiggja eða fá'].forEach((value) => { const span = document.createElement('span'); span.textContent = value; key.appendChild(span); });
        const rows = document.createElement('div'); visible.forEach((activity) => rows.appendChild(this.buildConsentRow(activity, player, partner))); details.append(summary, key, rows); this.consentList.appendChild(details);
      });
      const hasChoices = this.consentList.querySelector('[data-permission-key]') !== null;
      this.privateBoundaries.hidden = !hasChoices; this.noPrivateBoundaries.hidden = hasChoices; this.privateBoundaryNote.hidden = !hasChoices;
      this.bindExclusiveAccordions(this.consentList); this.updateConsentCounts();
    }

    bindExclusiveAccordions(root) { root.querySelectorAll('details').forEach((details) => details.addEventListener('toggle', () => { if (details.open) root.querySelectorAll('details').forEach((other) => { if (other !== details) other.open = false; }); })); }
    toggleAllChoices(root, selector, button, callback) { const inputs = [...root.querySelectorAll(selector)]; if (!inputs.length) return; const shouldSelect = !inputs.every((input) => input.checked); inputs.forEach((input) => { input.checked = shouldSelect; }); callback(); button?.focus({ preventScroll: true }); }
    updateBulkButton(button, inputs) { const allSelected = inputs.length > 0 && inputs.every((input) => input.checked); if (!button) return; button.hidden = inputs.length === 0; button.textContent = allSelected ? 'Hreinsa val' : 'Velja allt'; button.setAttribute('aria-pressed', String(allSelected)); }
    updateConsentCounts() { const all = [...this.consentList.querySelectorAll('[data-permission-key]')]; this.consentList.querySelectorAll('details').forEach((details) => { const inputs = [...details.querySelectorAll('[data-permission-key]')]; const count = inputs.filter((input) => input.checked).length; const status = details.querySelector('.lovely-game__group-status'); if (status) status.textContent = count ? `${count} af ${inputs.length} ${count === 1 ? 'valið' : 'valin'}` : 'Ekkert valið'; }); this.updateBulkButton(this.consentToggle, all); }

    buildConsentRow(activity, player, partner) {
      const row = document.createElement('div'); row.className = `lovely-game__consent-row${activity.selectionMode === 'personal' ? ' is-personal' : ''}`; row.setAttribute('role', 'group');
      const title = document.createElement('div'); title.className = 'lovely-game__consent-label'; title.textContent = activity.label; row.appendChild(title);
      if (activity.selectionMode === 'personal') {
        row.appendChild(this.makeChoice({ id: `${this.dataset.sectionId}-${this.state.setupPlayer}-${activity.key}-personal`, label: activity.single || activity.label, checked: Boolean(this.privateDraftPermissions[activity.key]?.personal), dataset: { permissionKey: activity.key, permissionDirection: 'personal' } })); return row;
      }
      ['give', 'receive'].forEach((direction) => {
        if (!this.directionAvailable(activity, player, partner, direction)) { const empty = document.createElement('span'); empty.className = 'lovely-game__choice-unavailable'; empty.textContent = 'Á ekki við'; row.appendChild(empty); return; }
        row.appendChild(this.makeChoice({ id: `${this.dataset.sectionId}-${this.state.setupPlayer}-${activity.key}-${direction}`, label: activity[direction] || (direction === 'give' ? 'Gefa' : 'Þiggja'), checked: Boolean(this.privateDraftPermissions[activity.key]?.[direction]), dataset: { permissionKey: activity.key, permissionDirection: direction } }));
      });
      return row;
    }

    nextPlayer() { this.state.setupPlayer = 1; this.showPrivateLevel(); }
    returnToSecondPrivateChoice() { this.state.setupPlayer = 1; this.showPrivateLevel(); }
    updateEffectiveMax() { this.state.effectiveMax = Math.min(...this.state.players.map((player) => Number(player.maxLevel || 1))); }

    equipmentVisible(item) { return Number(item.minLevel || 1) <= this.state.effectiveMax && (!item.targetAnatomy?.length || this.state.players.some((player) => this.hasAnatomy(player, item.targetAnatomy))); }
    hasVisibleEquipment() { return this.config.equipment.some((item) => this.equipmentVisible(item)); }
    cleanEquipment() { const allowed = new Set(this.config.equipment.filter((item) => this.equipmentVisible(item)).map((item) => item.key)); this.state.equipment = new Set([...this.state.equipment].filter((key) => allowed.has(key))); }

    goToSharedChoices() {
      this.cleanEquipment();
      if (this.hasVisibleEquipment()) { this.renderEquipment(); this.showScreen('equipment'); return; }
      this.state.equipment.clear(); this.renderLengths(); this.showScreen('length');
    }

    returnFromLength() {
      if (this.hasVisibleEquipment()) { this.renderEquipment(); this.showScreen('equipment'); return; }
      this.returnToSecondPrivateChoice();
    }

    updateSettingsAvailability() {
      if (this.settingsEquipmentButton) this.settingsEquipmentButton.hidden = !this.hasVisibleEquipment();
    }

    renderEquipment() {
      this.cleanEquipment(); this.equipmentList.replaceChildren();
      let visibleGroupCount = 0;
      this.config.equipmentGroups.forEach((groupData) => {
        const items = this.config.equipment.filter((item) => item.group === groupData.key && this.equipmentVisible(item)); if (!items.length) return;
        const details = document.createElement('details'); details.className = 'lovely-game__equipment-group'; details.open = visibleGroupCount === 0; visibleGroupCount += 1; const summary = document.createElement('summary'); const summaryTitle = document.createElement('strong'); summaryTitle.textContent = groupData.label; const status = document.createElement('span'); status.className = 'lovely-game__group-status'; summary.append(summaryTitle, status);
        const grid = document.createElement('div'); grid.className = 'lovely-game__equipment-grid'; items.forEach((item) => { const choice = this.makeChoice({ id: `${this.dataset.sectionId}-equipment-${item.key}`, label: item.label, description: this.publicDescription(item.description, item.key), value: item.key, checked: this.state.equipment.has(item.key), dataset: { equipmentKey: item.key } }); choice.classList.add('lovely-game__equipment-choice'); grid.appendChild(choice); });
        details.append(summary, grid); this.equipmentList.appendChild(details);
      });
      this.bindExclusiveAccordions(this.equipmentList); this.updateEquipmentCounts();
    }

    publicDescription(description, stableKey = '') { const value = String(description || '').trim(); if (!value) return ''; const normalized = value.toLowerCase().replaceAll('-', '_'); const key = String(stableKey || '').toLowerCase().replaceAll('-', '_'); if (normalized === key || (/^[a-z0-9_]+$/.test(normalized) && normalized.includes('_'))) return ''; return value; }
    updateEquipmentCounts() { const all = [...this.equipmentList.querySelectorAll('[data-equipment-key]')]; const total = all.filter((input) => input.checked).length; this.equipmentSummary.textContent = total ? `${total} ${total === 1 ? 'hlutur valinn' : 'hlutir valdir'}` : 'Ekkert valið'; this.equipmentList.querySelectorAll('details').forEach((details) => { const count = [...details.querySelectorAll('[data-equipment-key]')].filter((input) => input.checked).length; details.querySelector('.lovely-game__group-status').textContent = count ? `${count} ${count === 1 ? 'valið' : 'valin'}` : 'Ekkert valið'; }); this.updateBulkButton(this.equipmentToggle, all); }

    saveEquipment() { this.state.equipment = new Set([...this.equipmentList.querySelectorAll('[data-equipment-key]:checked')].map((input) => input.value)); if (this.settingsMode) return this.showScreen('settings'); this.renderLengths(); this.showScreen('length'); }

    renderLengths() {
      this.lengthList.replaceChildren(); this.config.lengths.forEach((length) => { const choice = this.makeChoice({ id: `${this.dataset.sectionId}-length-${length.key}`, label: length.label, description: length.description || '', type: 'radio', name: `${this.dataset.sectionId}-length`, value: length.total, checked: Number(this.state.gameLength) === Number(length.total), dataset: { gameLength: length.key } }); choice.classList.add('lovely-game__length-choice'); const count = document.createElement('span'); count.className = 'lovely-game__length-count'; count.innerHTML = `<strong>${Number(length.total)}</strong><small>spil</small>`; const status = document.createElement('span'); status.className = 'lovely-game__length-status'; status.textContent = 'Valið'; choice.append(count, status); this.lengthList.appendChild(choice); }); this.updateLengthStates();
    }

    updateLengthStates() { this.lengthList.querySelectorAll('.lovely-game__length-choice').forEach((choice) => choice.classList.toggle('is-selected', Boolean(choice.querySelector('input:checked')))); }

    saveLength() { const selected = this.lengthList.querySelector('input:checked'); this.state.gameLength = Number(selected?.value || this.config.lengths[0]?.total || 10); if (this.settingsMode) return this.showScreen('settings'); this.startGame(); }

    buildLevelPlan(totalCards = this.state.gameLength - (this.state.hasFinale ? 1 : 0), minimumLevel = 1) {
      const pools = this.config.levels
        .filter((level) => Number(level.value) >= Number(minimumLevel) && Number(level.value) <= this.state.effectiveMax)
        .sort((a, b) => Number(a.value) - Number(b.value))
        .map((level) => ({ level: Number(level.value), capacity: this.getEligibleCards(level.value).length, count: 0 }))
        .filter((item) => item.capacity > 0);
      let remaining = Math.min(Math.max(0, Number(totalCards)), pools.reduce((sum, item) => sum + item.capacity, 0));
      pools.forEach((item) => { if (remaining > 0) { item.count = 1; remaining -= 1; } });
      while (remaining > 0) {
        let added = false;
        for (let index = pools.length - 1; index >= 0 && remaining > 0; index -= 1) {
          if (pools[index].count >= pools[index].capacity) continue;
          pools[index].count += 1; remaining -= 1; added = true;
        }
        if (!added) break;
      }
      return pools.map(({ level, count }) => ({ level, count })).filter((item) => item.count > 0);
    }

    startGame() {
      this.state.currentPlanIndex = 0; this.state.levelProgress = 0; this.state.cardsShown = 0; this.state.usedIds = new Set(); this.state.phase = 'standard'; this.state.lastActorIndex = null; this.state.lastCardTheme = '';
      this.state.startedAt = Date.now(); this.analyticsLevelsSeen = new Set();
      if (this.completeMessage) this.completeMessage.textContent = 'Viljið þið spila aftur með sama vali eða breyta því?';
      this.state.hasFinale = this.hasEligibleFinale(this.state.effectiveMax); this.state.levelPlan = this.buildLevelPlan(); this.state.currentLevel = this.state.levelPlan[0]?.level || 1;
      this.state.plannedTotal = this.state.levelPlan.reduce((sum, item) => sum + Number(item.count || 0), 0) + (this.state.hasFinale ? 1 : 0);
      this.setGameMode(true);
      this.publishGameEvent('start', {
        game_length: Number(this.state.gameLength || 0),
        maximum_level: Number(this.state.effectiveMax || 0),
        maximum_level_name: this.levelName(this.state.effectiveMax),
        equipment_selected_count: this.state.equipment.size,
        finale_available: this.state.hasFinale ? 1 : 0
      });
      this.publishSelectionAnalytics('start');
      if (!this.state.levelPlan.length) { if (this.state.hasFinale) this.startFinale(this.state.effectiveMax); else this.showNoCards(); return; }
      this.showScreen('play'); this.showNextStandardCard();
    }

    resumeGame() {
      const previousLevel = Math.min(this.state.currentLevel, this.state.effectiveMax);
      this.state.hasFinale = this.hasEligibleFinale(this.state.effectiveMax);
      const remainingCards = Math.max(0, this.state.gameLength - this.state.cardsShown - (this.state.hasFinale ? 1 : 0));
      if (!remainingCards) return this.state.hasFinale ? this.startFinale(this.state.effectiveMax) : this.finishWithoutFinale();
      this.state.levelPlan = this.buildLevelPlan(remainingCards, previousLevel); this.state.plannedTotal = this.state.cardsShown + this.state.levelPlan.reduce((sum, item) => sum + Number(item.count || 0), 0) + (this.state.hasFinale ? 1 : 0); this.state.currentPlanIndex = 0; this.state.currentLevel = this.state.levelPlan[0]?.level || previousLevel; this.state.levelProgress = 0; this.state.phase = 'standard'; this.showScreen('play'); this.showNextStandardCard();
    }

    boundaryAllowed(player, key, role) {
      const activity = this.config.activities.find((item) => item.key === key); const permission = player.permissions[key]; if (!permission) return false;
      return activity?.selectionMode === 'personal' ? Boolean(permission.personal) : Boolean(permission[role]);
    }

    getEligibleCards(level, stage = 'standard') {
      return this.cards.flatMap((card) => {
        if (card.stage !== stage || Number(card.level) !== Number(level) || this.state.usedIds.has(card.id)) return [];
        if (!card.equipmentAll.every((key) => this.state.equipment.has(key)) || (card.equipmentAny.length && !card.equipmentAny.some((key) => this.state.equipment.has(key)))) return [];
        const orientations = [[0, 1], [1, 0]].filter(([actorIndex, receiverIndex]) => {
          const actor = this.state.players[actorIndex]; const receiver = this.state.players[receiverIndex];
          if (!this.hasAnatomy(actor, card.actorAnatomy) || !this.hasAnatomy(receiver, card.receiverAnatomy)) return false;
          return card.actorBoundaries.every((key) => this.boundaryAllowed(actor, key, 'give')) && card.receiverBoundaries.every((key) => this.boundaryAllowed(receiver, key, 'receive'));
        });
        return orientations.length ? [{ ...card, orientations }] : [];
      });
    }

    weightedChoice(cards) { const total = cards.reduce((sum, card) => sum + card.weight, 0); let pick = Math.random() * total; for (const card of cards) { pick -= card.weight; if (pick <= 0) return card; } return cards[cards.length - 1]; }

    cardTheme(card) {
      const requirements = [...card.actorBoundaries, ...card.receiverBoundaries, ...card.equipmentAll, ...card.equipmentAny];
      if (requirements.length) return requirements.sort().join('|');
      const text = String(card.text || '').toLocaleLowerCase('is');
      const themes = [
        ['nudd', /nudd|nudda/], ['kossar', /koss|kyss/], ['munnmök', /munnmök|sleiktu|sjúgðu/],
        ['spurningar', /segðu|nefndu|ræddu|spurning/], ['föt', /föt|klæða|undirföt/], ['stjórn', /skipun|stjórn|hlutverk/]
      ];
      return themes.find(([, pattern]) => pattern.test(text))?.[0] || '';
    }

    chooseCard(cards) {
      const varied = this.state.lastCardTheme ? cards.filter((card) => this.cardTheme(card) !== this.state.lastCardTheme) : cards;
      return this.weightedChoice(varied.length ? varied : cards);
    }

    chooseOrientation(card) {
      const alternating = this.state.lastActorIndex === null ? card.orientations : card.orientations.filter(([actorIndex]) => actorIndex !== this.state.lastActorIndex);
      const choices = alternating.length ? alternating : card.orientations;
      return choices[Math.floor(Math.random() * choices.length)];
    }

    showNextStandardCard() {
      this.state.phase = 'standard'; const available = this.getEligibleCards(this.state.currentLevel);
      if (!available.length) { this.continueAfterExhaustion(); return; }
      this.presentCard(this.chooseCard(available));
    }

    skipCard() {
      if (this.state.phase === 'finale') return;
      this.publishGameEvent('card_skip', {
        card_number: this.state.currentCard?.number || '',
        card_level: Number(this.state.currentCard?.level || 0),
        card_position: Number(this.state.cardsShown || 0)
      });
      const replacement = this.getEligibleCards(this.state.currentLevel);
      if (replacement.length) { this.presentCard(this.chooseCard(replacement), { countProgress: false }); return; }
      this.consumeCard();
    }

    consumeCard() {
      if (this.state.phase === 'finale') { this.publishGameComplete('finale'); return this.showScreen('complete'); }
      this.state.levelProgress += 1;
      const planned = this.state.levelPlan[this.state.currentPlanIndex];
      if (!planned || this.state.levelProgress >= planned.count) this.moveToNextLevelOrFinale(); else this.showNextStandardCard();
    }

    moveToNextLevelOrFinale() {
      this.state.currentPlanIndex += 1; const next = this.state.levelPlan[this.state.currentPlanIndex];
      if (!next) return this.state.hasFinale ? this.startFinale(this.state.effectiveMax) : this.finishWithoutFinale();
      this.state.currentLevel = next.level; this.state.levelProgress = 0; this.showNextStandardCard();
    }

    nextPlayablePlanIndex() { return this.state.levelPlan.findIndex((item, index) => index > this.state.currentPlanIndex && this.getEligibleCards(item.level).length); }

    showExhaustedLevel() { this.exhaustedMessage.textContent = this.config.copy.levelExhausted; this.showScreen('exhausted'); }

    continueAfterExhaustion() {
      const nextIndex = this.nextPlayablePlanIndex(); if (nextIndex < 0) return this.state.hasFinale ? this.startFinale(this.state.effectiveMax) : this.finishWithoutFinale();
      this.state.currentPlanIndex = nextIndex; this.state.currentLevel = this.state.levelPlan[nextIndex].level; this.state.levelProgress = 0; this.showScreen('play'); this.showNextStandardCard();
    }

    advanceLevel() {
      if (this.state.phase !== 'standard') return;
      const nextLevel = this.config.levels.map((item) => Number(item.value)).filter((value) => value > this.state.currentLevel && value <= this.state.effectiveMax).sort((a, b) => a - b)[0];
      if (!nextLevel) return;
      const remainingStandard = Math.max(0, this.state.plannedTotal - this.state.cardsShown - (this.state.hasFinale ? 1 : 0));
      this.state.levelPlan = this.buildLevelPlan(remainingStandard, nextLevel);
      this.state.plannedTotal = this.state.cardsShown + this.state.levelPlan.reduce((sum, item) => sum + Number(item.count || 0), 0) + (this.state.hasFinale ? 1 : 0);
      this.state.currentPlanIndex = 0; this.state.currentLevel = this.state.levelPlan[0]?.level || nextLevel; this.state.levelProgress = 0; this.settingsMode = false;
      if (!this.state.levelPlan.length) return this.state.hasFinale ? this.startFinale(this.state.effectiveMax) : this.finishWithoutFinale();
      this.showScreen('play'); this.showNextStandardCard();
    }

    hasEligibleFinale(targetLevel) {
      for (let level = Number(targetLevel); level >= 1; level -= 1) if (this.getEligibleCards(level, 'finale').length) return true;
      return false;
    }

    startFinale(targetLevel) {
      for (let level = Number(targetLevel); level >= 1; level -= 1) {
        const finales = this.getEligibleCards(level, 'finale');
        if (finales.length) { this.settingsMode = false; this.state.phase = 'finale'; this.state.currentLevel = level; this.showScreen('play'); this.presentCard(this.chooseCard(finales)); return; }
      }
      this.finishWithoutFinale();
    }

    showNoCards() { this.publishGameEvent('no_eligible_cards', { maximum_level: Number(this.state.effectiveMax || 0), game_length: Number(this.state.gameLength || 0) }); this.emptyMessage.textContent = this.config.copy.noCards; this.showScreen('empty'); }

    finishWithoutFinale() {
      if (this.completeMessage) this.completeMessage.textContent = 'Leiknum er lokið. Ekkert ónotað Lokaatriði passaði við núverandi val.';
      this.publishGameComplete('without_finale');
      this.showScreen('complete');
    }

    presentCard(card, options = {}) {
      this.resetTimer();
      const orientation = options.orientation || this.chooseOrientation(card); const actor = this.state.players[orientation[0]]; const receiver = this.state.players[orientation[1]];
      card.tokens = { actor: actor.name, receiver: receiver.name }; card.playerIndexes = { actor: orientation[0], receiver: orientation[1] }; this.state.currentCard = card;
      if (options.countProgress !== false) this.state.cardsShown += 1;
      this.state.usedIds.add(card.id); this.state.lastActorIndex = orientation[0]; this.state.lastCardTheme = this.cardTheme(card);
      const level = this.config.levels.find((item) => Number(item.value) === Number(card.level)); const total = Math.max(1, Number(this.state.plannedTotal || this.state.gameLength)); const current = Math.min(this.state.cardsShown, total);
      this.playCount.textContent = this.state.phase === 'finale' ? `${this.config.copy.finale} · spil ${current}` : this.replaceTokens(this.config.copy.cardProgress, { level: level?.name || '', current, total });
      this.currentLevelName.textContent = this.state.phase === 'finale' ? this.config.copy.finale : level?.name || ''; this.currentLevelDescription.textContent = this.state.phase === 'finale' ? (this.config.adminSettings?.finaleIntro || 'Eitt síðasta spil sem virðir öll val ykkar.') : level?.intro || level?.description || '';
      this.cardCategory.textContent = this.state.phase === 'finale' ? this.config.copy.finale : level?.name || ''; this.cardNumber.textContent = String(card.number).startsWith('#') ? card.number : `#${card.number}`;
      this.renderLevelProgress(); this.renderCardContext(card); this.renderTokenText(this.cardText, card.text, card.tokens, card.playerIndexes); this.renderCardMeta(card); this.prepareTimer(Number(card.timer || 0));
      const hasNextLevel = this.state.levelPlan.some((item, index) => index > this.state.currentPlanIndex && item.level > this.state.currentLevel); this.advanceLevelButton.hidden = this.state.phase === 'finale' || !hasNextLevel || this.config.adminSettings?.allowLevelJump === false; this.advanceLevelButton.textContent = this.config.copy.nextLevel;
      this.finaleButton.hidden = this.state.phase === 'finale' || !this.state.hasFinale; this.finaleButton.textContent = this.config.copy.finishSprint; this.skipButton.hidden = this.state.phase === 'finale'; this.nextButton.textContent = this.state.phase === 'finale' ? this.config.copy.finish : 'Næsta spil';
      this.gameCard.dataset.level = String(card.level); this.gameCard.classList.toggle('is-finale', this.state.phase === 'finale'); this.gameCard.classList.toggle('is-copy-medium', String(card.text || '').length > 105); this.gameCard.classList.toggle('is-copy-long', String(card.text || '').length > 165); this.gameCard.classList.remove('is-entering'); void this.gameCard.offsetWidth; this.gameCard.classList.add('is-entering'); this.gameCard.focus({ preventScroll: true });
      if (!this.analyticsLevelsSeen.has(Number(card.level))) {
        this.analyticsLevelsSeen.add(Number(card.level));
        this.publishGameEvent('level_reached', { level: Number(card.level), level_name: level?.name || '', card_position: current });
      }
      this.publishGameEvent('card_view', {
        card_number: card.number,
        card_level: Number(card.level || 0),
        card_level_name: level?.name || '',
        card_stage: this.state.phase,
        card_position: current,
        planned_total: total,
        has_shop_link: card.shopUrls.find(Boolean) ? 1 : 0
      });
      this.persistSession();
    }

    normalizeTokens(template) { return String(template || '').replace(/\((actor|receiver)\)/gi, '{$1}'); }

    appendTokenText(node, template, tokens, indexes) {
      const parts = this.normalizeTokens(template).split(/(\{actor\}|\{receiver\})/gi);
      parts.forEach((part) => { const token = part.toLowerCase(); const key = token === '{actor}' ? 'actor' : token === '{receiver}' ? 'receiver' : null; if (!key) { node.appendChild(document.createTextNode(part)); return; } const span = document.createElement('span'); span.className = `lovely-game__player-name lovely-game__player-name--${indexes[key]}`; span.textContent = tokens[key]; node.appendChild(span); });
    }

    renderTokenText(node, template, tokens, indexes) { node.replaceChildren(); this.appendTokenText(node, template, tokens, indexes); }

    renderLevelProgress() {
      this.levelProgress.replaceChildren();
      this.config.levels.forEach((level) => {
        const value = Number(level.value); const item = document.createElement('div'); item.className = 'lovely-game__level-progress-item';
        if (value < this.state.currentLevel) item.classList.add('is-complete');
        if (value === this.state.currentLevel) { item.classList.add('is-current'); item.setAttribute('aria-current', 'step'); }
        if (value > this.state.effectiveMax) item.classList.add('is-locked');
        const dot = document.createElement('span'); dot.className = 'lovely-game__level-progress-dot'; dot.setAttribute('aria-hidden', 'true');
        const label = document.createElement('strong'); label.textContent = level.name; item.append(dot, label); this.levelProgress.appendChild(item);
      });
    }

    requiredItems(card) {
      const required = [...card.equipmentAll]; const chosenAny = card.equipmentAny.find((key) => this.state.equipment.has(key)); if (chosenAny) required.push(chosenAny);
      return [...required.map((key) => this.config.equipment.find((item) => item.key === key)?.label || key), ...(card.otherNeeded ? [card.otherNeeded] : [])];
    }

    summarizeLabels(labels, limit = 2) { const unique = [...new Set(labels.filter(Boolean))]; if (unique.length <= limit) return unique.join(', '); return `${unique.slice(0, limit).join(', ')} +${unique.length - limit}`; }

    appendContextPill(label, value) {
      if (!value) return;
      const pill = document.createElement('span'); pill.className = 'lovely-game__context-pill'; const strong = document.createElement('strong'); strong.textContent = `${label}:`; pill.append(strong, document.createTextNode(` ${value}`)); this.cardContext.appendChild(pill);
    }

    renderCardContext(card) {
      this.cardContext.replaceChildren();
      const boundaryLabels = [...card.actorBoundaries, ...card.receiverBoundaries].map((key) => this.config.activities.find((item) => item.key === key)?.label || key);
      this.appendContextPill(this.config.copy.choiceContext || 'Val ykkar', this.summarizeLabels(boundaryLabels));
      const equipmentKeys = [...card.equipmentAll]; const chosenAny = card.equipmentAny.find((key) => this.state.equipment.has(key)); if (chosenAny) equipmentKeys.push(chosenAny);
      const equipmentLabels = equipmentKeys.map((key) => this.config.equipment.find((item) => item.key === key)?.label || key);
      this.appendContextPill(this.config.copy.equipmentNeeded || 'Búnaður', this.summarizeLabels(equipmentLabels));
      if (card.otherNeeded) this.appendContextPill(this.config.copy.otherNeeded || 'Hafið við höndina', card.otherNeeded);
      this.cardContext.hidden = !this.cardContext.childElementCount;
    }

    renderCardMeta(card) {
      this.cardMeta.replaceChildren();
      if (card.tip) this.cardMeta.appendChild(this.metaParagraph(this.config.copy.tip, card.tip, card));
      if (card.safety) { const safety = this.metaParagraph(this.config.copy.safety, card.safety, card); safety.className = 'lovely-game__safety'; this.cardMeta.appendChild(safety); }
      const shopUrl = card.shopUrls.find(Boolean); if (shopUrl && this.config.adminSettings?.showShopLinks !== false) { const link = document.createElement('a'); link.className = 'lovely-game__shop-link'; link.href = shopUrl; link.target = '_blank'; link.rel = 'noopener'; link.textContent = card.shopButtonText || this.config.copy.shopCard; this.cardMeta.appendChild(link); }
      this.cardMeta.hidden = !this.cardMeta.childElementCount;
    }

    metaParagraph(label, value, card) { const paragraph = document.createElement('p'); const strong = document.createElement('strong'); strong.textContent = label; paragraph.append(strong, document.createTextNode(' ')); this.appendTokenText(paragraph, value, card.tokens, card.playerIndexes); return paragraph; }
    settingsSignature() {
      return JSON.stringify({ players: this.state.players.map((player) => ({ maxLevel: player.maxLevel, permissions: player.permissions })), equipment: [...this.state.equipment].sort(), gameLength: this.state.gameLength });
    }
    openSettings() { this.settingsMode = true; this.settingsReturnScreen = this.currentScreen === 'settings' ? 'play' : this.currentScreen; this.settingsSnapshot = this.settingsSignature(); this.updateSettingsAvailability(); this.renderLevelProgress(); this.showScreen('settings'); }
    editBeforeReplay() { this.settingsStartsNewGame = true; this.openSettings(); }
    replayGame() { this.settingsStartsNewGame = false; this.startGame(); }
    editPrivateChoices() { this.state.setupPlayer = 0; this.showPrivateLevel(); }
    closeSettings() {
      this.updateEffectiveMax(); this.cleanEquipment(); const changed = this.settingsSignature() !== this.settingsSnapshot; this.settingsMode = false;
      if (this.settingsStartsNewGame) { this.settingsStartsNewGame = false; this.startGame(); }
      else if (changed) { this.publishGameEvent('settings_update', { game_length: Number(this.state.gameLength || 0), maximum_level: Number(this.state.effectiveMax || 0), equipment_selected_count: this.state.equipment.size }); this.publishSelectionAnalytics('update'); this.resumeGame(); } else this.showScreen(this.settingsReturnScreen || 'play');
      this.persistSession();
    }

    clearTimerInterval() { if (this.timerInterval) window.clearInterval(this.timerInterval); this.timerInterval = null; }
    resetTimer() { this.clearTimerInterval(); this.timerRemaining = 0; this.timerRunning = false; this.timerEndsAt = 0; }
    prepareTimer(seconds) { this.resetTimer(); this.timerRemaining = Math.max(0, Number(seconds || 0)); this.timer.hidden = !this.timerRemaining; if (this.timerRemaining) { this.timerDisplay.textContent = this.formatTime(this.timerRemaining); this.timerButton.textContent = this.config.copy.timerStart; } }
    primeAudio() { const AudioContext = window.AudioContext || window.webkitAudioContext; if (!AudioContext) return; if (!this.audioContext) this.audioContext = new AudioContext(); if (this.audioContext.state === 'suspended') this.audioContext.resume().catch(() => {}); }
    toggleTimer() {
      if (!this.timerRemaining && !this.timerRunning) return;
      this.primeAudio();
      if (this.timerRunning) { this.pauseTimer(); this.timerButton.textContent = this.config.copy.timerStart; this.persistSession(); return; }
      this.timerRunning = true; this.timerEndsAt = Date.now() + (this.timerRemaining * 1000); this.timerButton.textContent = this.config.copy.timerPause;
      this.clearTimerInterval(); this.timerInterval = window.setInterval(() => this.updateTimer(), 250); this.persistSession();
    }
    updateTimer(signal = true) {
      if (!this.timerRunning || !this.timerEndsAt) return;
      this.timerRemaining = Math.max(0, Math.ceil((this.timerEndsAt - Date.now()) / 1000)); this.timerDisplay.textContent = this.formatTime(this.timerRemaining);
      if (this.timerRemaining <= 0) this.finishTimer(signal);
    }
    pauseTimer() { if (this.timerRunning) this.updateTimer(false); this.clearTimerInterval(); this.timerRunning = false; this.timerEndsAt = 0; }
    finishTimer(signal = true) { this.clearTimerInterval(); this.timerRunning = false; this.timerEndsAt = 0; this.timerRemaining = 0; this.timerDisplay.textContent = '00:00'; this.timerButton.textContent = this.config.copy.timerDone; if (signal) this.signalTimer(); this.persistSession(); }
    signalTimer() {
      if (navigator.vibrate) navigator.vibrate([180, 100, 180]);
      if (!this.audioContext) return;
      const now = this.audioContext.currentTime;
      [0, .22].forEach((delay, index) => { const oscillator = this.audioContext.createOscillator(); const gain = this.audioContext.createGain(); oscillator.frequency.value = index ? 880 : 660; gain.gain.setValueAtTime(.0001, now + delay); gain.gain.exponentialRampToValueAtTime(.16, now + delay + .02); gain.gain.exponentialRampToValueAtTime(.0001, now + delay + .18); oscillator.connect(gain).connect(this.audioContext.destination); oscillator.start(now + delay); oscillator.stop(now + delay + .2); });
    }

    async requestWakeLock() {
      if (!this.gameModeActive || document.visibilityState !== 'visible' || !navigator.wakeLock || this.wakeLock) return;
      try { this.wakeLock = await navigator.wakeLock.request('screen'); this.wakeLock.addEventListener('release', () => { this.wakeLock = null; }, { once: true }); } catch (error) { this.wakeLock = null; }
    }
    async releaseWakeLock() { if (!this.wakeLock) return; const lock = this.wakeLock; this.wakeLock = null; try { await lock.release(); } catch (error) {} }
    onVisibilityChange() { if (document.visibilityState === 'visible') { this.updateTimer(); this.requestWakeLock(); } else { this.persistSession(); this.releaseWakeLock(); } }
    onBeforeUnload(event) { if (!this.gameModeActive) return; this.persistSession(); event.preventDefault(); event.returnValue = ''; }

    serializeState() {
      return {
        version: 12,
        savedAt: Date.now(),
        state: {
          ...this.state,
          players: this.state.players.map((player) => ({ ...player, anatomy: [...player.anatomy] })),
          equipment: [...this.state.equipment], usedIds: [...this.state.usedIds],
          currentCard: this.state.currentCard ? { id: this.state.currentCard.id, playerIndexes: this.state.currentCard.playerIndexes } : null
        },
        timer: { remaining: this.timerRemaining, running: this.timerRunning, endsAt: this.timerEndsAt }
      };
    }
    persistSession() { if (!this.gameModeActive || !this.state.currentCard) return; try { sessionStorage.setItem(this.sessionKey, JSON.stringify(this.serializeState())); } catch (error) {} }
    readSession() { try { const saved = JSON.parse(sessionStorage.getItem(this.sessionKey) || 'null'); return saved?.version === 12 && saved?.state?.currentCard ? saved : null; } catch (error) { return null; } }
    clearSession() { try { sessionStorage.removeItem(this.sessionKey); } catch (error) {} }
    offerSessionRestore() {
      const saved = this.readSession(); if (!saved) return;
      const names = (saved.state.players || []).map((player) => player.name).filter(Boolean);
      if (this.restoreSummary) this.restoreSummary.textContent = names.length === 2 ? `${names[0]} og ${names[1]} voru í miðjum leik.` : 'Leikur var í gangi í þessum flipa.';
      this.showScreen('restore', { scroll: false });
    }
    restoreSession() {
      const saved = this.readSession(); if (!saved) { this.showScreen('intro'); return; }
      const raw = saved.state; const baseCard = this.cards.find((card) => card.id === raw.currentCard.id);
      if (!baseCard) { this.clearSession(); this.showScreen('intro'); return; }
      this.state = { ...this.freshState(), ...raw, players: raw.players.map((player) => ({ ...player, anatomy: new Set(player.anatomy || []) })), equipment: new Set(raw.equipment || []), usedIds: new Set(raw.usedIds || []), currentCard: null };
      this.analyticsLevelsSeen = new Set([...this.state.usedIds].map((id) => Number(this.cards.find((card) => card.id === id)?.level || 0)).filter(Boolean));
      this.nameInputs.forEach((input, index) => { input.value = this.state.players[index]?.name || ''; this.playerHeadings[index].textContent = input.value || `Aðili ${index + 1}`; });
      this.renderProfiles(); this.setGameMode(true); this.showScreen('play', { scroll: false });
      const indexes = raw.currentCard.playerIndexes || { actor: 0, receiver: 1 }; const orientation = [Number(indexes.actor), Number(indexes.receiver)]; const orientations = [orientation];
      this.presentCard({ ...baseCard, orientations }, { orientation, countProgress: false });
      const timer = saved.timer || {}; this.timerRemaining = Math.max(0, Number(timer.remaining || 0)); this.timerRunning = Boolean(timer.running); this.timerEndsAt = Number(timer.endsAt || 0);
      if (Number(baseCard.timer || 0)) { this.timer.hidden = false; this.timerDisplay.textContent = this.formatTime(this.timerRemaining); this.timerButton.textContent = this.timerRunning ? this.config.copy.timerPause : (this.timerRemaining ? this.config.copy.timerStart : this.config.copy.timerDone); }
      if (this.timerRunning) { this.updateTimer(); if (this.timerRunning) this.timerInterval = window.setInterval(() => this.updateTimer(), 250); }
      this.persistSession();
    }
    discardSession() { this.clearSession(); this.showScreen('intro'); }

    requestExitGame() { this.exitReturnFocus = document.activeElement; this.exitDialog.hidden = false; this.exitDialog.querySelector('[data-action="cancel-exit"]')?.focus({ preventScroll: true }); }
    closeExitDialog() { if (!this.exitDialog) return; this.exitDialog.hidden = true; this.exitReturnFocus?.focus({ preventScroll: true }); }
    resetGame() { if (this.gameModeActive && this.currentScreen !== 'complete') this.publishGameEvent('exit', { cards_shown: Number(this.state.cardsShown || 0), current_level: Number(this.state.currentLevel || 0), elapsed_seconds: this.gameElapsedSeconds() }); this.resetTimer(); this.clearSession(); this.closeExitDialog(); this.setGameMode(false); this.state = this.freshState(); this.analyticsLevelsSeen = new Set(); this.settingsMode = false; this.settingsStartsNewGame = false; if (this.completeMessage) this.completeMessage.textContent = 'Viljið þið spila aftur með sama vali eða breyta því?'; this.nameInputs.forEach((input, index) => { input.value = ''; this.playerHeadings[index].textContent = `Aðili ${index + 1}`; }); this.renderProfiles(); this.showScreen('intro'); }
    replaceTokens(text, tokens) { return Object.entries(tokens).reduce((result, [key, value]) => result.replaceAll(`{${key}}`, String(value)), text || ''); }
    formatTime(seconds) { const safe = Math.max(0, Math.ceil(Number(seconds) || 0)); return `${Math.floor(safe / 60).toString().padStart(2, '0')}:${(safe % 60).toString().padStart(2, '0')}`; }
  }

  if (!customElements.get('lovely-game-deck')) customElements.define('lovely-game-deck', LovelyGameDeck);
})();
